import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { eventDOMTypeName, EventType, eventTypeName } from '../enums';
import { Event } from '../models';
import { EndpointActions } from '../redux/actions';
import { getDateTimeOffset, onWindowBeforeUnload, hasValue } from '../utils';
import { RootState } from '../redux/models';
import { selectSessionId } from '../redux/selectors/selectSessionId';
import { selectCollectAnalytics } from '../redux/selectors';
import { withGoogleEvents } from '../hoc/withGoogleEvents';
import { IAnalytics } from '../models/UtilityPackMessage';
import { functionLibrary } from '../utils/FunctionLibrary';
import { isIOS } from 'react-device-detect';
import { onWindowPageHide } from '../utils/onWindowPageHide';
import { onDocumentVisibilityChange, onDocumentWebKitVisibilityChange } from '../utils/onDocumentVisibilityChange';
import { onWindowUnload } from '../utils/onWindowUnload';

export const eventServiceRef = React.createRef<EventService>();

export interface EventServiceProps {
	postEvents: typeof EndpointActions.actionCreators.postEvents;
	currentSceneIndex?: number;
	currentTime: number;
	duration: number;
	maximumTime: number;
	sessionId: string;
	collectAnalytics: boolean;
	sendGoogleAnalytics: (data: IAnalytics) => void;
}

interface EventServiceState {
	hasHandledUnload: boolean;
}

class EventService extends React.Component<EventServiceProps, EventServiceState> {
	private _buffer: Event[] = [];
	private readonly _firstFlushDelayMs = 500;
	private readonly _iosPostIntervalMs = 500;
	private readonly _postIntervalMs = 5000;
	private _lastPostAtMs = 0;
	private _flushTimerId: number | undefined;
	private _hasHandledUnload = false;

	constructor(props: EventServiceProps) {
		super(props);

		this.state = {
			hasHandledUnload: false,
		};

		// Listen to window events to handle unload
		onWindowBeforeUnload.subscribe(() => this.handleOnUnloadPage());
		onWindowPageHide.subscribe((ev: any) => {
			// On iOS Safari, `pagehide` frequently has `persisted=true` due to BFCache and can still
			// be the only reliable signal before the page gets suspended. Prefer to flush anyway.
			if (!isIOS && ev && typeof ev.persisted === 'boolean' && ev.persisted) return;
			this.handleOnUnloadPage();
		});

		// iOS Safari fallback
		if (isIOS) {
			var onVisibilityHiddenIOS = () => {
				const doc: any = document as any;
				const isHidden = document.visibilityState === 'hidden' || doc.webkitVisibilityState === 'hidden' || document.hidden === true || doc.webkitHidden === true;
				if (isHidden) this.handleOnUnloadPage();
			};
			onDocumentVisibilityChange.subscribe(() => onVisibilityHiddenIOS());
			onDocumentWebKitVisibilityChange.subscribe(() => onVisibilityHiddenIOS());
			onWindowUnload.subscribe(() => this.handleOnUnloadPage());
		}
	}

	public logEvent = (event: Event) => {
		this._buffer.push(event);
		if (this.resolveEventTriggerDom(event)) this.triggerEvent(event);

		// Force send for critical terminal events
		const forceSend = event.type === 3 || event.type === 39;

		this.requestFlush(true, forceSend);
	};

	public componentWillUnmount = () => {
		this.clearFlushTimer();
	};

	public shouldComponentUpdate = () => {
		return false;
	};

	public render = () => {
		return null;
	};

	public handleOnUnloadPage = () => {
		// Avoid relying on React state during dismissal/suspension (especially on iOS).
		if (this._hasHandledUnload) return;
		this._hasHandledUnload = true;

		const { currentSceneIndex, currentTime, duration, maximumTime } = this.props;
		if (hasValue(currentSceneIndex)) {
			this._buffer.push({
				type: EventType.VideoUnloaded,
				eventDateTimeOffset: getDateTimeOffset(),
				scene: currentSceneIndex,
				sceneTime: currentTime,
				duration: duration,
				maximumTime: maximumTime,
			});
		}
		this._buffer.push({
			type: EventType.PlayerLeave,
			eventDateTimeOffset: getDateTimeOffset(),
		});
		this.requestFlush(false, true);
	};

	private clearFlushTimer = () => {
		if (this._flushTimerId !== undefined) {
			window.clearTimeout(this._flushTimerId);
			this._flushTimerId = undefined;
		}
	};

	/**
	 * Schedules a buffered post so requests are rate-limited.
	 * - Default behavior: first post occurs ~500ms after the first buffered event.
	 * - After the first post: at most every 5s on most devices; on iOS we keep a 500ms cadence to
	 *   reduce dependence on unreliable unload events.
	 * - Force: flush immediately (used on unload).
	 */
	private requestFlush = (async: boolean, force: boolean = false) => {
		if (!this.props.collectAnalytics) {
			// Preserve existing behavior: when analytics collection is disabled, we don't keep accumulating events.
			this._buffer = [];
			this.clearFlushTimer();
			return;
		}

		if (this._buffer.length === 0) return;

		if (force) {
			this.clearFlushTimer();
			this._lastPostAtMs = Date.now();
			this.sendEvents(async);
			return;
		}

		// Only ever keep one scheduled flush.
		if (this._flushTimerId !== undefined) return;

		const nowMs = Date.now();
		const postIntervalMs = isIOS ? this._iosPostIntervalMs : this._postIntervalMs;
		const delayMs = this._lastPostAtMs === 0 ? this._firstFlushDelayMs : Math.max(0, postIntervalMs - (nowMs - this._lastPostAtMs));
		this._flushTimerId = window.setTimeout(() => {
			this._flushTimerId = undefined;
			this._lastPostAtMs = Date.now();
			this.sendEvents(true);
		}, delayMs);
	};

	private sendEvents = (async: boolean) => {
		if (this._buffer.length > 0) {
			const events = this._buffer;
			this._buffer = [];

			//only post events if collect analytics is true
			if (this.props.collectAnalytics) {
				const revertBuffer = () => {
					this._buffer.push(...events);
				};

				// Send buffer
				this.props.postEvents({ async, events }).then(
					(response) => {
						// If status code is not success revert items to buffer
						if (!(response.status >= 200 && response.status < 300)) {
							revertBuffer();
							this.requestFlush(true);
						}
					},
					() => {
						revertBuffer();
						this.requestFlush(true);
					},
				);

				// Send analytics
				this.sendAnalytics(events);
			}
		}
	};

	private triggerEvent = (event: Event) => {
		var eventDOMName = eventDOMTypeName[event.type];
		if (eventDOMName) functionLibrary.createEvent(eventDOMName);
	};

	private sendAnalytics = (events: Event[]) => {
		events.forEach((event: any) => {
			var eventName = eventTypeName[event.type];
			//only send events that are parsed
			if (eventName) {
				const dataToSend: IAnalytics = {
					type: 'analytics',
					analytics: 'VideoSmart',
					sessionId: this.props.sessionId,
					name: eventName,
					description: {
						maxValue: event.maxValue && event.maxValue,
						value: event.value && event.value,
						previousScene: event.previousScene && event.previousScene,
						tag: event.tag && event.tag,
						language: event.language && event.language,
						duration: event.duration && event.duration,
						seekedFrom: event.seekedFrom && event.seekedFrom,
						seekedTo: event.seekedTo && event.seekedTo,
						maximumTime: event.maximumTime && event.maximumTime,
						volumeFrom: event.volumeFrom && event.volumeFrom,
						volumeTo: event.volumeTo && event.volumeTo,
					},
				};
				this.props.sendGoogleAnalytics(dataToSend);
			}
		});
	};

	private resolveEventTriggerDom = (event: Event) => {
		switch (event.type) {
			case EventType.PlayerExitFullscreen:
			case EventType.PlayerEnterFullscreen:
				return false;
			default:
				return true;
		}
	};
}

const mapStateToProps = (state: RootState): Pick<EventServiceProps, 'currentSceneIndex' | 'currentTime' | 'duration' | 'maximumTime' | 'sessionId' | 'collectAnalytics'> => ({
	currentSceneIndex: state.context.currentSceneIndex,
	currentTime: state.video.currentTime,
	duration: state.video.duration,
	maximumTime: state.video.maximumTime,
	sessionId: selectSessionId(state),
	collectAnalytics: selectCollectAnalytics(state),
});

const mapDispatchToProps = (dispatch: Dispatch): Pick<EventServiceProps, 'postEvents'> => ({
	postEvents: bindActionCreators(EndpointActions.actionCreators.postEvents, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true })(withGoogleEvents(EventService));
